import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TokenContext } from "../../service/context/tokencontent";
import { profile } from "../../common/svg";
import { useSelector, useDispatch } from "react-redux";
import { setLanguage } from "../../service/context/languageSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { menuItems } from "../../common/menus";
import SecureImage from "../../components/BucketImage/Image";
import { toggleSidebar } from "../../redux/container/sidebarSlice";

const Sidebar: React.FC = () => {
  const { setToken } = useContext(TokenContext)!;
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfilePanel, setShowProfilePanel] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { isMobileMenuOpen, isCollapsed } = useSelector((state: RootState) => state.sidebar);

  const logout = () => {
    localStorage.removeItem("user_data_motsop");
    localStorage.removeItem("motosop");
    localStorage.removeItem("booking_draft");
    setToken("");
    navigate("/login");
  };

  const profileData = localStorage.getItem("user_data_motsop") || "";
  const user = JSON.parse(profileData);

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    if (window.innerWidth < 768) dispatch(toggleSidebar());
  };

  return (
    <>
      {/* Sidebar */}
      <div
        className={`h-screen bg-white flex flex-col transition-all font-poppins duration-300 z-40 
        ${isCollapsed ? "w-20" : "w-[208px]"}
        ${isMobileMenuOpen ? "fixed left-0 top-0 bottom-0" : "fixed -left-full md:left-0 top-0 bottom-0"}
        md:relative md:left-0`}
      >
        {/* Header */}
        <div className="flex-grow overflow-y-auto flex flex-col">
          <div className="p-4 flex items-center justify-between">
            {!isCollapsed && (
              <h1 className="text-[28px] font-poppins leading-[42px] text-center font-[600]">
                Motosop
              </h1>
            )}
            {isMobileMenuOpen && (
              <button onClick={() => dispatch(toggleSidebar())}>
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Menu */}
          <nav className="p-2 mx-4 flex-grow">
            {menuItems.map((item) => (
              <button
                key={item.title}
                onClick={() => handleMenuItemClick(item.path)}
                className={`w-full flex items-center px-4 gap-3 p-2 rounded-[10px] mb-1 transition-colors ${
                  location.pathname.startsWith(item.path)
                    ? "bg-[#444FBD] text-white"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <span className="flex-shrink-0">
                  <SecureImage
                    fileKey={item.icon}
                    alt={item.title}
                    width="25px"
                    className={`h-[25.21px] w-[25.21px] transition ${
                      location.pathname.startsWith(item.path) ? "filter invert-[1] brightness-[0]" : ""
                    }`}
                  />
                </span>
                {!isCollapsed && (
                  <span className="flex-1 text-[14.05px] font-[500] text-left">
                    {item.title}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Profile Section - Fixed at bottom */}
        <div className="p-4 border-t mt-auto">
          <div
            className="flex items-center p-2 gap-2 w-full cursor-pointer"
            onClick={() => setShowProfilePanel(!showProfilePanel)}
          >
            <div
              className="w-10 h-10 rounded-full p-[1px] flex-shrink-0 overflow-hidden"
              style={{
                background:
                  "linear-gradient(51.02deg, #444FBD 13.6%, #FFFFFF 85.56%)",
              }}
            >
              <SecureImage
                fileKey={profile}
                alt="User"
                width="80px"
                className="w-full h-full object-cover"
              />
            </div>
            {!isCollapsed && (
              <div className="overflow-hidden">
                <p className="font-medium text-sm truncate">{user?.firstname +" "+ user?.lastname}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Slide-in Panel */}
      {showProfilePanel && (
        <div className="fixed bottom-0 left-0 z-50 w-full md:pl-[10px] flex">
          <div className="w-full md:w-[320px] bg-white border-[1px] rounded-[10px] shadow-xl max-h-[90vh] overflow-y-auto p-6 relative">
            {/* Close (Mobile) */}
            <button
              onClick={() => setShowProfilePanel(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>

            <div className="flex flex-col items-center gap-4">
              <div className="w-24 h-24 rounded-full overflow-hidden border">
                <SecureImage
                  fileKey={profile}
                  alt="User"
                  width="100px"
                  className="w-full h-full object-cover"
                />
              </div>
              <h2 className="text-xl font-semibold">{user?.firstname +" "+ user?.lastname}</h2>
            </div>

            <div className="mt-6 space-y-3 text-sm text-gray-700">
              <div>
                <p className="text-gray-500">Email</p>
                <p className="mt-1">
                {user?.email}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Phone number</p>
                <p className="mt-1">+91 {user?.mobile}</p>
              </div>
            </div>

            <div className="mt-8 border-t pt-6">
              <button
                onClick={logout}
                className="text-red-500 hover:text-red-700 transition font-medium"
              >
                Log out
              </button>
            </div>
          </div>

          {/* Overlay (Mobile Only) */}
          <div
            className="flex-1 bg-black bg-opacity-30 md:hidden"
            onClick={() => setShowProfilePanel(false)}
          />
        </div>
      )}
    </>
  );
};

export default Sidebar;